/*
 * Copyright (C) 2018 Spanning Cloud Apps. LLC  All rights reserved.
 */
import './wizard.tpl.html';

/*
 * This module holds components used in building a wizard that takes a user through a series of steps to accomplish a
 * task.
 */

/**
 * Defines the sbsf-wizard directive.
 *
 * @returns {Object} - The directive settings
 */
export function directive() {
  return {
    controller: 'WizardCtrl',
    templateUrl: 'shared/wizard/wizard.tpl.html',
    restrict: 'E',
    scope: {
      // The name of the ui-view for the page content
      view: '@'
    }
  };
}

// Define the services that will be injected into the controller.
controller.$inject = [
  '$uibModal',
  '$q',
  '$rootScope',
  '$scope',
  '$state',
  '$timeout',
  'UtilService',
  'WizardService',
  'VIEW_STATES',
  'confirmModalUtils'
];

/**
 * The controller for the directive.
 */
export function controller(
  $uibModal,
  $q,
  $rootScope,
  $scope,
  $state,
  $timeout,
  UtilService,
  WizardService,
  VIEW_STATES,
  confirmModalUtils
) {
  // Publish functions in the scope
  $scope.getCurrentStep = getCurrentStep;
  $scope.getPageHeaderText = getPageHeaderText;
  $scope.getStepKeys = getStepKeys;
  $scope.isCompletedStep = isCompletedStep;
  $scope.isCurrentStep = isCurrentStep;
  $scope.isFirstStep = isFirstStep;
  $scope.isLastStep = isLastStep;
  $scope.isNextEnabled = isNextEnabled;
  $scope.isNextVisible = isNextVisible;
  $scope.isPrevEnabled = isPrevEnabled;
  $scope.isUnknownStep = isUnknownStep;
  $scope.cancel = cancel;
  $scope.next = next;
  $scope.prev = prev;
  $scope.goToStep = goToStep;
  $scope.getPageCustomButtons = getPageCustomButtons;

  // These are exposed for testing
  $scope.handleStateChangeStart = handleStateChangeStart;

  var FORWARD_CLASS = 'sbsf-forward';
  var BACKWARD_CLASS = 'sbsf-backward';

  // The default direction is to move forward through the steps
  $scope.animation = FORWARD_CLASS;

  // Intercept state changes away from the wizard and ask for confirmation
  $scope.removeStateChangeListener = $rootScope.$on('$stateChangeStart', handleStateChangeStart);

  // Remove the state change listener when the page unloads.
  $scope.$on('$destroy', function () {
    if ($scope.removeStateChangeListener) {
      $scope.removeStateChangeListener();
    }
    WizardService.reset();
  });

  /**
   * Returns the index of the current step in the process.
   *
   * @returns {number} the 0-based index of the current step.
   */
  function getCurrentStep() {
    return WizardService.getCurrentStep();
  }

  /**
   * Returns the header text for the current page.
   *
   * @returns {string} - the header text for the current page.
   */
  function getPageHeaderText() {
    return WizardService.getCurrentPage().getHeaderText();
  }

  /**
   * Returns the step keys.
   *
   * @returns {string[]} the I18N keys of the titles of the steps in the wizard.
   */
  function getStepKeys() {
    return WizardService.getStepKeys();
  }

  /**
   * Determines whether a step is a completed step.
   *
   * @param {number} step - The step index to be checked.
   * @returns {boolean} true if the supplied step is a completed step, false otherwise.
   */
  function isCompletedStep(step) {
    return WizardService.isCompletedStep(step);
  }

  /**
   * Determines whether a step is the current step.
   *
   * @param {number} step - The step index to be checked.
   * @returns {boolean} true if the supplied step is the current step, false otherwise.
   */
  function isCurrentStep(step) {
    return WizardService.isCurrentStep(step);
  }

  /**
   * Determines whether a step represents the first step.
   *
   * @param {number} step - The step index to be checked.
   * @returns {boolean} true if the supplied step is the first step, false otherwise.
   */
  function isFirstStep(step) {
    return WizardService.isFirstStep(step);
  }

  /**
   * Determines whether a step represents the last step.
   *
   * @param {number} step - The step index to be checked.
   * @returns {boolean} true if the supplied step is the last step, false otherwise.
   */
  function isLastStep(step) {
    return WizardService.isLastStep(step);
  }

  /**
   * Determines whether a step represents that the remaining steps are not yet known.
   *
   * @param {number} step - The step index to be checked.
   * @returns {boolean} true if the supplied step represents and unknown step, false otherwise.
   */
  function isUnknownStep(step) {
    return WizardService.isUnknownStep(step);
  }

  /**
   * Determines whether the next button should be enabled.
   *
   * @returns {boolean} true if the next button should be enabled, false otherwise.
   */
  function isNextEnabled() {
    return WizardService.isNextEnabled();
  }

  /**
   * Determines whether the next button should be visible.
   *
   * @returns {boolean} true if the next button should be visible, false otherwise.
   */
  function isNextVisible() {
    return WizardService.isNextVisible();
  }

  /**
   * Determines whether the prev button should be enabled.
   *
   * @returns {boolean} true if the prev button should be enabled, false otherwise.
   */
  function isPrevEnabled() {
    return WizardService.isPrevEnabled();
  }

  /**
   * Advances to the next page.
   */
  function next() {
    // Save the state params so we can pass them to the next state
    var stateParams = WizardService.getCurrentPage().getStateParams();

    return WizardService.next().then(function () {
      // Go to the new current state, forwarding the state params from the current page to the new state
      // Go to the new state after a timeout so the animation has a chance to get set up
      $scope.animation = FORWARD_CLASS;
      return $timeout(function () {
        $state.go(WizardService.getCurrentState(), stateParams);
      });
    });
  }

  /**
   * Returns to the previous page.
   */
  function prev() {
    // Save the state params so we can pass them to the next state
    var stateParams = WizardService.getCurrentPage().getStateParams();

    return WizardService.prev().then(function () {
      // Go to the new current state, forwarding the state params from the current page to the new state
      // Go to the new state after a timeout so the animation has a chance to get set up
      $scope.animation = BACKWARD_CLASS;
      return $timeout(function () {
        $state.go(WizardService.getCurrentState(), stateParams);
      });
    });
  }

  /**
   * Moves to a specific page in the history
   *
   * @param {number} step - The index of the page to go back to.
   */
  function goToStep(step) {
    if (step === getCurrentStep()) {
      return $q.when();
    }

    // Save the state params so we can pass them to the next state
    var stateParams = WizardService.getCurrentPage().getStateParams();

    return WizardService.goToStep(step).then(function () {
      // Go to the new current state, forwarding the state params from the current page to the new state
      // Go to the new state after a timeout so the animation has a chance to get set up
      $scope.animation = BACKWARD_CLASS;
      return $timeout(function () {
        $state.go(WizardService.getCurrentState(), stateParams);
      });
    });
  }

  /**
   * Returns the array of custom operations.
   *
   * @returns {object[]}
   */
  function getPageCustomButtons() {
    return WizardService.getCurrentPage().getCustomButtons();
  }

  /**
   * Handles an angular-ui $stateChangeStart event. If we're leaving the wizard we may need to ask for confirmation.
   * See the angular-ui docs for a description of the parameters.
   *
   * @param {*} event
   * @param {*} toState
   * @param {*} toParams
   * @param {*} fromState
   * @param {*} fromParams
   * @param {*} options
   */
  function handleStateChangeStart(event, toState, toParams, fromState, fromParams, options) {
    // If canceling is true we've already come through here.
    if ($scope.canceling) {
      return;
    }

    // If we're staying within the wizard there's no need to confirm
    var statePrefix = WizardService.getStatePrefix();
    if (statePrefix && fromState.name.startsWith(statePrefix) && toState.name.startsWith(statePrefix)) {
      return;
    }

    // If the wizard has not been touched there's no need to confirm
    if (!WizardService.getTouched()) {
      return;
    }

    // Don't propagate the state change any further. We'll do that from the cancellation modal if the user confirms.
    event.preventDefault();

    // Tell our main app the state change has been interrupted so it can reset any loading mask
    $rootScope.$broadcast('stateChangeStopped', toState, toParams, fromState, fromParams, options);

    // Call cancel through scope so we can stub it during testing
    $scope.cancel({ showConfirm: true, nextState: toState.name });
  }

  /**
   * Exits the wizard, optionally asking for confirmation.
   *
   * @params {Object} [params] - Options
   * @params {boolean} [params.showConfirm] - Boolean flag to show modal or not on cancel.
   * @params {string} [params.confirmTextKey] - The key of the text resource of the text to show in the confirm
   *   modal.
   * @params {string} [params.confirmButtonTextKey] - The key of the text resource of the confirm button text to show
   *   in the confirm modal.
   * @params {string} [params.extraHeight] - Flag to add or not add extra height to modal.
   * @params {string} [params.nextState] - The state to go to after confirmation.
   * @returns {Promise} a promise that is resolved when the user dismisses the confirmation.
   */
  function cancel(params) {
    params = params || {};
    // If showConfirm has not been passed determine whether the wizard has been touched. If not there's no need to
    // confirm.
    if (!_.has(params, 'showConfirm')) {
      params.showConfirm = WizardService.getTouched();
    }

    $scope.canceling = true;

    // If we don't need to confirm go directly to the specified state. If no state is specified all wizards exit to
    // the dashboard (the ADMIN_HOME state).
    if (!params.showConfirm) {
      $state.go(params.nextState || VIEW_STATES.ADMIN_HOME);
      return $q.when();
    }

    // Scroll to where the modal will appear
    UtilService.scrollHome();

    return confirmModalUtils
      .openConfirmModal({
        windowClass: 'sbsf-confirm-modal-window' + (params.extraHeight ? ' sbsf-extra-height-modal' : ''),
        confirmTextKey: WizardService.getCancelConfirmTextKey(),
        titleTextKey: 'CONTINUE'
      })
      .then(function (confirmed) {
        if (confirmed) {
          //  If no state is specified all wizards exit to the dashboard (the ADMIN_HOME state).
          $state.go(params.nextState || VIEW_STATES.ADMIN_HOME);
        } else {
          $scope.canceling = false;
        }
      })
      .catch(function () {
        // Rejection handler - the user decided not to cancel
        $scope.canceling = false;
      });
  }
}
